import React, { useState } from "react";
import { History, HelpCircle, ShieldCheck, Bell, BellOff, BookOpen, ArrowLeft, Sparkles } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setSavedLang, SUPPORTED_LANGS } from "@/i18n";
import UserMenu from "@/components/UserMenu";
import { useAuth } from "@/context/AuthContext";
import { openOnboardingTour } from "@/components/OnboardingTour";
import { isSoundEnabled, setSoundEnabled, playBeep } from "@/lib/beep";

export default function Header({ right }) {
  const loc = useLocation();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [soundOn, setSoundOn] = useState(() => isSoundEnabled());

  const switchLang = (lng) => {
    if (lng === i18n.language) return;
    setSavedLang(lng);
    i18n.changeLanguage(lng);
  };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    setSoundEnabled(next);
    // Preview the chime when enabling so the user knows what they'll hear.
    if (next) playBeep();
  };

  return (
    <header className="sticky top-0 z-30 backdrop-blur-xl bg-[color:var(--bg-base)]/80 border-b border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="w-9 h-9 flex items-center justify-center shrink-0">
            <img
              src="/favicon.svg"
              alt=""
              className="w-9 h-9 rounded-lg shadow-lg"
              draggable={false}
            />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-display font-bold text-white tracking-tight">Spectraloom</span>
            <span className="tiny-caps text-[9px] -mt-0.5">Mix &amp; Master Analyzer</span>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <div
            className="flex items-center rounded-full border border-white/10 overflow-hidden text-[11px] leading-none"
            data-testid="lang-selector"
            aria-label={t("header.language")}
          >
            {SUPPORTED_LANGS.map((lng) => (
              <button
                key={lng}
                type="button"
                onClick={() => switchLang(lng)}
                data-testid={`lang-${lng}`}
                className={`px-2.5 py-1.5 uppercase tracking-wider transition-colors ${
                  i18n.language === lng
                    ? "bg-white/10 text-white"
                    : "text-[color:var(--fg-muted)] hover:text-white"
                }`}
              >
                {lng}
              </button>
            ))}
          </div>
          {loc.pathname.startsWith("/blog") && (
            <a
              href="https://spectraloom.app/"
              data-testid="back-to-app"
              className="inline-flex items-center gap-2 rounded-full border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 text-[#a78bfa] hover:text-white hover:bg-[#8B5CF6]/20 hover:border-[#8B5CF6]/70 transition-colors px-3.5 py-1.5 text-xs font-semibold no-underline"
            >
              <ArrowLeft size={14} />
              {i18n.language === "en" ? "Back to app" : "Torna all'app"}
            </a>
          )}
          {loc.pathname !== "/history" && (
            <Link
              to="/history"
              className="btn-ghost text-sm flex items-center gap-2 !py-2 !px-3"
              data-testid="nav-history"
            >
              <History size={14} /> {t("header.history")}
            </Link>
          )}
          {!loc.pathname.startsWith("/blog") && (
            <Link
              to="/blog"
              className="btn-ghost text-sm flex items-center gap-2 !py-2 !px-3"
              data-testid="nav-blog"
            >
              <BookOpen size={14} />
              <span className="hidden md:inline">{t("blog.nav")}</span>
            </Link>
          )}
          {loc.pathname !== "/pricing" && (
            <Link
              to="/pricing"
              className="btn-ghost text-sm flex items-center gap-2 !py-2 !px-3"
              data-testid="nav-pricing"
            >
              <Sparkles size={14} />
              <span className="hidden md:inline">{t("pricing.nav")}</span>
            </Link>
          )}
          {user?.is_admin && loc.pathname !== "/admin" && (
            <Link
              to="/admin"
              className="btn-ghost text-sm flex items-center gap-2 !py-2 !px-3 text-emerald-300 hover:text-emerald-200"
              data-testid="nav-admin"
              title={t("admin.nav_link")}
            >
              <ShieldCheck size={14} />
              <span className="hidden md:inline">{t("admin.nav_link")}</span>
            </Link>
          )}
          <button
            type="button"
            onClick={toggleSound}
            className="btn-ghost text-sm flex items-center justify-center !py-2 !px-2.5"
            data-testid="header-sound-toggle"
            aria-label={soundOn ? t("header.sound_on") : t("header.sound_off")}
            title={soundOn ? t("header.sound_on") : t("header.sound_off")}
            aria-pressed={soundOn}
          >
            {soundOn ? (
              <Bell size={14} className="text-emerald-300" />
            ) : (
              <BellOff size={14} className="text-[color:var(--fg-muted)]" />
            )}
          </button>
          <button
            type="button"
            onClick={openOnboardingTour}
            className="btn-ghost text-sm flex items-center gap-2 !py-2 !px-3"
            data-testid="header-replay-tour"
            aria-label={t("header.replay_tour")}
            title={t("header.replay_tour")}
          >
            <HelpCircle size={14} />
            <span className="hidden md:inline">{t("header.replay_tour")}</span>
          </button>
          <UserMenu />
          {right}
        </div>
      </div>
    </header>
  );
}
